import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CateringForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    const inquiryData = {
      customer_name: formData.get('name'),
      customer_email: formData.get('email'),
      customer_phone: formData.get('phone'),
      event_date: formData.get('event_date'),
      event_type: formData.get('event_type'),
      guest_count: parseInt(formData.get('guest_count')),
      location: formData.get('location'),
      menu_preferences: formData.get('menu_preferences') || null,
      budget: formData.get('budget') || null,
      additional_notes: formData.get('additional_notes') || null
    };

    try {
      await axios.post(`${BACKEND_URL}/api/catering-inquiries`, inquiryData);
      toast.success("Catering inquiry submitted! We'll contact you soon.");
      e.target.reset();
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="catering" className="py-20 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-24">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Catering Services</h2>
          <p className="text-xl text-gray-600">
            Let Chef Eric make your event unforgettable with professional catering
          </p>
        </div>

        <Card className="border-2 border-emerald-200">
          <CardHeader className="bg-emerald-50">
            <CardTitle className="text-2xl">Request a Catering Quote</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="event_date">Event Date *</Label>
                  <Input
                    id="event_date"
                    name="event_date"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="event_type">Event Type *</Label>
                  <select
                    id="event_type"
                    name="event_type"
                    required
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select event type</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="birthday">Birthday Party</option>
                    <option value="private_party">Private Party</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="guest_count">Number of Guests *</Label>
                  <Input
                    id="guest_count"
                    name="guest_count"
                    type="number"
                    min="10"
                    placeholder="50"
                    required
                    className="mt-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="location">Event Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="123 Main St, City, State"
                    required
                    className="mt-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="menu_preferences">Menu Preferences (Optional)</Label>
                  <textarea
                    id="menu_preferences"
                    name="menu_preferences"
                    placeholder="Any specific dishes or dietary requirements?"
                    rows="3"
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <Label htmlFor="budget">Budget Range (Optional)</Label>
                  <select
                    id="budget"
                    name="budget"
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select budget range</option>
                    <option value="under_1000">Under $1,000</option>
                    <option value="1000_2500">$1,000 - $2,500</option>
                    <option value="2500_5000">$2,500 - $5,000</option>
                    <option value="over_5000">Over $5,000</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="additional_notes">Additional Notes (Optional)</Label>
                  <textarea
                    id="additional_notes"
                    name="additional_notes"
                    placeholder="Any other details we should know..."
                    rows="3"
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
              >
                {isSubmitting ? "Submitting..." : "Submit Catering Inquiry"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
